import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip
} from '@mui/material';
import {
  AccountBalance as WalletIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchBranchWallets, fetchWallets, assignWalletToBranch, unassignWalletFromBranch, updateBranchWalletNumber } from '../walletApi';
import { fetchBranchById } from '../branchesApi';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const BranchWallets = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasPermission = user && user.Permission === true;
  
  const [branchWallets, setBranchWallets] = useState([]);
  const [allWallets, setAllWallets] = useState([]);
  const [branchInfo, setBranchInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    walletId: '',
    walletNumber: '',
    walletAccountName: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [branch, assigned, all] = await Promise.all([
        fetchBranchById(id),
        fetchBranchWallets(id),
        fetchWallets()
      ]);
      setBranchInfo(branch);
      setBranchWallets(assigned || []);
      setAllWallets(all || []);
    } catch (error) {
      console.error('Error loading data:', error);
      Swal.fire(t('Error'), t('DataLoadFail') || 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (wallet = null) => {
    if (wallet) {
      setEditMode(true);
      setFormData({
        walletId: wallet.WalletId,
        walletNumber: wallet.WalletNumber || '',
        walletAccountName: wallet.WalletAccountName || ''
      });
    } else {
      setEditMode(false);
      setFormData({ 
        walletId: '', 
        walletNumber: '',
        walletAccountName: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({ 
      walletId: '', 
      walletNumber: '',
      walletAccountName: ''
    });
  };

  const handleSubmit = async () => {
    if (!formData.walletId || !formData.walletNumber) {
      Swal.fire(t('Error'), t('PleaseSelectWalletAndNumber') || 'Please select wallet and enter number', 'error');
      return;
    }

    try {
      if (editMode) {
        await updateBranchWalletNumber(
          id, 
          formData.walletId, 
          formData.walletNumber,
          formData.walletAccountName || null
        );
        Swal.fire(t('Success'), t('WalletInfoUpdated') || 'Wallet information updated successfully', 'success');
      } else {
        await assignWalletToBranch(
          id, 
          formData.walletId, 
          formData.walletNumber,
          formData.walletAccountName || null
        );
        Swal.fire(t('Success'), t('WalletAssigned') || 'Wallet assigned successfully', 'success');
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      Swal.fire(t('Error'), error.response?.data?.message || t('OperationFailed'), 'error');
    }
  };

  const handleUnassign = (walletId) => {
    Swal.fire({
      title: t('Are_You_Sure'),
      text: t('UnassignWalletWarning') || 'This will remove the wallet from this branch',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('Yes_Remove_It') || 'Yes, remove it',
      cancelButtonText: t('Cancel')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await unassignWalletFromBranch(id, walletId);
          Swal.fire(t('Removed'), t('WalletUnassigned') || 'Wallet removed from branch', 'success');
          loadData();
        } catch (error) {
          Swal.fire(t('Error'), error.response?.data?.message || t('OperationFailed'), 'error');
        }
      }
    });
  };

  // Get available wallets (not assigned yet)
  const availableWallets = allWallets.filter(
    w => !branchWallets.some(bw => bw.WalletId === w.Id)
  );

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title={`${t('BranchWallets') || 'Branch Payment Wallets'} - ${branchInfo?.name || branchInfo?.Name || ''}`}
          subheader={t('ManageBranchWalletsDesc') || 'Assign payment wallets with their numbers for this branch'}
          avatar={<WalletIcon color="primary" fontSize="large" />}
          action={
            hasPermission && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                {t('AssignWallet') || 'Assign Wallet'}
              </Button>
            )
          }
          sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
        />
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('WalletName') || 'Wallet Name'}</TableCell>
                  <TableCell>{t('WalletNumber') || 'Wallet Number'}</TableCell>
                  <TableCell>{t('AccountName') || 'Account Name'}</TableCell>
                  <TableCell>{t('Image')}</TableCell>
                  <TableCell>{t('Description')}</TableCell>
                  {hasPermission && <TableCell align="right">{t('Actions')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {branchWallets.map((bw) => (
                  <TableRow key={bw.Id}>
                    <TableCell>{bw.WalletName}</TableCell>
                    <TableCell>
                      <Chip label={bw.WalletNumber} color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {bw.WalletAccountName ? (
                        <Typography variant="body2">{bw.WalletAccountName}</Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {bw.WalletImage && (
                        <img 
                          src={bw.WalletImage} 
                          alt={bw.WalletName} 
                          style={{ width: 60, height: 40, objectFit: 'contain', borderRadius: 4 }} 
                        />
                      )}
                    </TableCell>
                    <TableCell>{bw.Description || '-'}</TableCell>
                    {hasPermission && (
                      <TableCell align="right">
                        <IconButton color="primary" onClick={() => handleOpenDialog(bw)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleUnassign(bw.WalletId)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {branchWallets.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={hasPermission ? 6 : 5} align="center">
                      {t('NoWalletsAssigned') || 'No wallets assigned to this branch yet'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Assign/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? t('EditWalletInfo') || 'Edit Wallet Information' : t('AssignWallet') || 'Assign Wallet'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              select
              fullWidth
              label={t('SelectWallet') || 'Select Wallet'}
              value={formData.walletId}
              onChange={(e) => setFormData({ ...formData, walletId: e.target.value })}
              disabled={editMode}
            >
              {(editMode ? allWallets : availableWallets).map((wallet) => (
                <MenuItem key={wallet.Id} value={wallet.Id}>
                  {wallet.Name}
                </MenuItem>
              ))}
            </TextField>
            
            <TextField
              fullWidth
              label={t('WalletNumber') || 'Wallet Number'}
              placeholder="e.g. 770123456"
              value={formData.walletNumber}
              onChange={(e) => setFormData({ ...formData, walletNumber: e.target.value })}
              helperText={t('EnterWalletNumberForBranch') || 'Enter the wallet account number for this branch'}
              required
            />

            <TextField
              fullWidth
              label={t('AccountName') || 'Account Name'}
              placeholder={t('EnterAccountNamePlaceholder') || 'e.g. John Doe or Store Name'}
              value={formData.walletAccountName}
              onChange={(e) => setFormData({ ...formData, walletAccountName: e.target.value })}
              helperText={t('EnterAccountNameHelper') || 'Enter the account holder name (optional)'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('Cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? t('Update') : t('Assign')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchWallets;